import FileDownloadIcon from "@mui/icons-material/FileDownload";
import CloseIcon from "@mui/icons-material/Close";
import FilterListIcon from "@mui/icons-material/FilterList";
import PrintIcon from "@mui/icons-material/Print";
import RefreshIcon from "@mui/icons-material/Refresh";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  GlobalStyles,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ListItemText from "@mui/material/ListItemText";
import moment from "moment";
import { useCallback, useMemo, useState, type Dispatch, type SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { useGetBranches } from "src/api/generic";
import { REPORT_BRANCH_MULTISELECT_ALL_SENTINEL } from "src/constants/reportBranchSelect";
import {
  useGetAggregatorsReport,
  useGetAggregatorsReportData,
  type AggregatorsReportDataPayload,
  type AggregatorsReportDataRow,
} from "src/api/reports";
import DateRangeInput from "src/shared/components/date-range";
import {
  ReportControlBar,
  ReportPageHeading,
  ReportSectionCard,
  reportLayoutTokens,
} from "src/shared/components/report-layout";
import { authState } from "src/store/auth";
import RightArrow from "src/assets/svg-icons/aggregators/right_arrow.svg";
import {
  downloadAsXLS,
  oneBranchHasPermission,
  specificBranchHasPermission,
} from "src/utils";
import { ReportsPermissions } from "src/constants/permissions";

type AggregatorsPlForm = {
  startDate: string;
  endDate: string;
  branchIds: number[];
};

const parseAggregatorsPlFilters = (sp: URLSearchParams): AggregatorsPlForm => {
  const b = sp.getAll("branches");
  return {
    startDate:
      sp.get("start_date") || moment().startOf("month").format("yyyy-MM-DD"),
    endDate:
      sp.get("end_date") || moment().endOf("month").format("yyyy-MM-DD"),
    branchIds: b.length ? b.map((x) => Number(x)).filter((n) => Number.isFinite(n)) : [],
  };
};

const formatMetricCell = (row: AggregatorsReportDataRow, raw: number | string) => {
  if (row.value_kind === "text") return String(raw ?? "");
  const n = Number(raw);
  if (!Number.isFinite(n)) return "—";
  const pctKeys = new Set([
    "cog_pct",
    "aggregator_commission_pct",
    "net_pct",
  ]);
  if (pctKeys.has(row.key)) {
    return new Intl.NumberFormat(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(n);
  }
  return new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
};

const AggregatorsPlReportPage = () => {
  const { t } = useTranslation("reports");
  const { t: generalT } = useTranslation("general");
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const auth = useRecoilValue(authState);
  const companyName = auth.user?.company?.name;

  const [appliedFilters, setAppliedFilters] = useState<AggregatorsPlForm>(() =>
    parseAggregatorsPlFilters(new URLSearchParams(searchParams.toString()))
  );
  const [draftFilters, setDraftFilters] = useState<AggregatorsPlForm>(() =>
    parseAggregatorsPlFilters(new URLSearchParams(searchParams.toString()))
  );

  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [modalDraft, setModalDraft] = useState<AggregatorsPlForm>(() =>
    parseAggregatorsPlFilters(new URLSearchParams(searchParams.toString()))
  );

  const { startDate, endDate, branchIds } = appliedFilters;

  const { data: branchesData } = useGetBranches();
  const branchesHavePermission =
    branchesData?.data.filter((br) =>
      specificBranchHasPermission(
        ReportsPermissions.viewThirdEyeReportDeliveryApps,
        br.id
      )
    ) ?? [];

  const {
    data: reportBody,
    isFetching,
    refetch,
  } = useGetAggregatorsReportData({
    start_date: startDate,
    end_date: endDate,
    branches: branchIds.length > 0 ? branchIds : undefined,
    enabled: true,
  });

  const { refetch: refetchDownload, isFetching: isDownloadLoading } =
    useGetAggregatorsReport({
      start_date: startDate,
      end_date: endDate,
      branches: branchIds.length > 0 ? branchIds : undefined,
      enabled: false,
    });

  const downloadReportsPermission = oneBranchHasPermission(
    ReportsPermissions.viewThirdEyeReportDeliveryApps
  );

  const payload = (reportBody as { data?: AggregatorsReportDataPayload } | null | undefined)
    ?.data;

  const aggregators = payload?.aggregators ?? [];
  const rows = payload?.rows ?? [];
  const compareHeading = payload?.compare_table_heading ?? t("aggsReport");

  const periodLabel = useMemo(
    () =>
      t("reportDateRange", {
        start: moment(startDate).format("D MMM YYYY"),
        end: moment(endDate).format("D MMM YYYY"),
      }),
    [t, startDate, endDate]
  );

  const openFilterModal = () => {
    setModalDraft({ ...draftFilters });
    setFilterModalOpen(true);
  };

  const cancelFilterModal = () => setFilterModalOpen(false);

  const commitFiltersFromModal = () => {
    setDraftFilters({ ...modalDraft });
    setFilterModalOpen(false);
  };

  const syncAppliedToUrl = useCallback(
    (next: AggregatorsPlForm) => {
      const p = new URLSearchParams();
      p.set("start_date", next.startDate);
      p.set("end_date", next.endDate);
      next.branchIds.forEach((id) => p.append("branches", String(id)));
      setSearchParams(p);
    },
    [setSearchParams]
  );

  const handleReportUpdate = () => {
    setAppliedFilters({ ...draftFilters });
    syncAppliedToUrl(draftFilters);
    void refetch();
  };

  const handlePrint = () => window.print();

  const handleDownload = useCallback(async () => {
    const result = await refetchDownload();
    const d = result.data as string | undefined;
    if (d != null) {
      downloadAsXLS(`\uFEFF${d}`, "aggregators-report");
    }
  }, [refetchDownload]);

  const tableMinWidth = useMemo(
    () => Math.max(aggregators.length * 140 + 280, 520),
    [aggregators.length]
  );

  const branchSelectControl = (
    value: AggregatorsPlForm["branchIds"],
    onChange: (ids: number[]) => void
  ) => (
    <FormControl size="small" sx={{ minWidth: 220 }}>
      <InputLabel id="agg-pl-branches" shrink>
        {generalT("branch")}
      </InputLabel>
      <Select<string[]>
        labelId="agg-pl-branches"
        multiple
        value={value.map((id) => String(id))}
        onChange={(e: SelectChangeEvent<string[]>) => {
          const v = e.target.value;
          const next = typeof v === "string" ? v.split(",") : v;
          const strs = next as string[];
          if (strs.includes(REPORT_BRANCH_MULTISELECT_ALL_SENTINEL)) {
            onChange([]);
            return;
          }
          const ids = strs.map(Number).filter((n) => n > 0);
          onChange(ids);
        }}
        input={<OutlinedInput label={generalT("branch")} />}
        renderValue={(selected) => {
          if (selected.length === 0) return t("allBranches");
          return selected
            .map(
              (idStr) =>
                branchesHavePermission.find((b) => String(b.id) === idStr)?.name ?? idStr
            )
            .join(", ");
        }}
      >
        <MenuItem
          key="__all-branches-agg-pl"
          value={REPORT_BRANCH_MULTISELECT_ALL_SENTINEL}
          sx={{
            borderBottom: "1px solid #EAECF0",
            bgcolor: "#F9FAFB",
          }}
        >
          <Typography fontWeight={600} variant="body2" component="span">
            {t("allBranches")}
          </Typography>
        </MenuItem>
        {branchesHavePermission.map((b) => (
          <MenuItem key={b.id} value={String(b.id)}>
            <Checkbox size="small" checked={value.includes(b.id)} />
            <ListItemText primary={b.name} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const dateRangeControl = (
    draft: AggregatorsPlForm,
    setDraft: Dispatch<SetStateAction<AggregatorsPlForm>>
  ) => (
    <Box sx={{ minWidth: 250 }}>
      <DateRangeInput
        startDate={draft.startDate ? moment(draft.startDate) : null}
        startDateId="agg-pl-start"
        endDate={draft.endDate ? moment(draft.endDate) : null}
        endDateId="agg-pl-end"
        onDatesChange={(arg: {
          startDate: moment.Moment | null;
          endDate: moment.Moment | null;
        }) => {
          const s = arg.startDate?.format("yyyy-MM-DD");
          const e = arg.endDate?.format("yyyy-MM-DD");
          if (s && e) {
            setDraft((prev) => ({ ...prev, startDate: s, endDate: e }));
          }
        }}
      />
    </Box>
  );

  return (
    <Box p={{ xs: 2, sm: "12px 32px 32px" }} maxWidth="100%">
      <GlobalStyles
        styles={{
          "@media print": {
            "body *": { visibility: "hidden" },
            "#aggregators-pl-print-root, #aggregators-pl-print-root *": {
              visibility: "visible",
            },
            "#aggregators-pl-print-root": {
              position: "absolute",
              left: 0,
              top: 0,
              width: "100%",
            },
          },
        }}
      />

      <Stack
        direction="row"
        alignItems="center"
        gap={1}
        mb={1.5}
        sx={{ "@media print": { display: "none" } }}
      >
        <Typography
          fontSize="0.9rem"
          color="#475467"
          onClick={() => navigate("/reports")}
          sx={{ cursor: "pointer" }}
        >
          {t("thirdEyeReport")}
        </Typography>
        <img src={RightArrow} alt="" style={{ width: 12, height: 12 }} />
        <Box borderRadius="4px" bgcolor="#EAECF0" px={1} py={0.5}>
          <Typography color="#344054" fontWeight={600} fontSize="0.85rem">
            {t("aggregatorsPnLReport")}
          </Typography>
        </Box>
      </Stack>

      <Box sx={{ "@media print": { display: "none" } }} mb={2}>
        <ReportControlBar
          left={
            <>
              {branchSelectControl(draftFilters.branchIds, (ids) =>
                setDraftFilters((p) => ({ ...p, branchIds: ids }))
              )}
              {dateRangeControl(draftFilters, setDraftFilters)}
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<FilterListIcon fontSize="small" />}
                onClick={openFilterModal}
                sx={{
                  height: 40,
                  minWidth: 100,
                  borderColor: "#D0D5DD",
                  color: "#344054",
                }}
              >
                {t("filter")}
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<RefreshIcon fontSize="small" />}
                onClick={handleReportUpdate}
                disabled={isFetching}
                sx={{ height: 40, minWidth: 120 }}
              >
                {t("reportUpdate")}
              </Button>
            </>
          }
          right={
            <>
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<PrintIcon fontSize="small" />}
                onClick={handlePrint}
                sx={{
                  height: 40,
                  minWidth: 100,
                  borderColor: "#D0D5DD",
                  color: "#344054",
                }}
              >
                {t("print")}
              </Button>
              <LoadingButton
                variant="outlined"
                startIcon={<FileDownloadIcon fontSize="small" />}
                onClick={handleDownload}
                loading={isDownloadLoading}
                disabled={!downloadReportsPermission}
                sx={{
                  height: 40,
                  minWidth: 100,
                  borderColor: "#D0D5DD",
                  color: "#344054",
                }}
              >
                {t("export")}
              </LoadingButton>
            </>
          }
        />
      </Box>

      <Dialog open={filterModalOpen} onClose={cancelFilterModal} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, pr: 1 }}>
          <FilterListIcon sx={{ color: "#475467", fontSize: 22 }} />
          <Typography component="span" fontWeight={700} flex={1}>
            {t("reportFilters")}
          </Typography>
          <IconButton aria-label="close" onClick={cancelFilterModal} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2}>
            {branchSelectControl(modalDraft.branchIds, (ids) =>
              setModalDraft((p) => ({ ...p, branchIds: ids }))
            )}
            {dateRangeControl(modalDraft, setModalDraft)}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 0, flexDirection: "column", gap: 1 }}>
          <Button
            variant="contained"
            fullWidth
            size="large"
            onClick={commitFiltersFromModal}
            disabled={isFetching}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            {t("applyFilters")}
          </Button>
          <Button color="inherit" fullWidth onClick={cancelFilterModal} sx={{ textTransform: "none" }}>
            {generalT("cancel")}
          </Button>
        </DialogActions>
      </Dialog>

      <Box id="aggregators-pl-print-root">
        <ReportPageHeading
          title={t("aggregatorsPnLReport")}
          subtitle={companyName ?? payload?.company_name}
          periodLabel={periodLabel}
        />

        <ReportSectionCard>
          {isFetching && !payload ? (
            <Box display="flex" justifyContent="center" py={8}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer sx={{ maxWidth: "100%", overflowX: "auto" }}>
              <Table
                size="small"
                stickyHeader
                sx={{ minWidth: tableMinWidth }}
              >
                <TableHead>
                  <TableRow
                    sx={{
                      "& th": {
                        bgcolor: reportLayoutTokens.tableHeaderBg,
                        color: reportLayoutTokens.tableHeaderText,
                        fontWeight: 700,
                        fontSize: "0.72rem",
                        textTransform: "uppercase",
                        borderBottom: reportLayoutTokens.cardBorder,
                        py: 1.5,
                        whiteSpace: "nowrap",
                      },
                    }}
                  >
                    <TableCell align="left" sx={{ fontWeight: 700 }}>
                      {compareHeading}
                    </TableCell>
                    {aggregators.map((a) => (
                      <TableCell key={a.id} align="right">
                        {a.name}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row, idx) => (
                    <TableRow
                      key={`${row.key}-${idx}`}
                      sx={{
                        "& td": {
                          borderColor: "#EEF0F2",
                          fontSize: "0.875rem",
                          color: "#101828",
                          py: 1.15,
                        },
                      }}
                    >
                      <TableCell align="left" sx={{ fontWeight: 500 }}>
                        {row.label}
                      </TableCell>
                      {(row.values ?? []).map((cell, i) => (
                        <TableCell
                          key={`${row.key}-c-${i}`}
                          align="right"
                          sx={{ fontWeight: 600 }}
                        >
                          {formatMetricCell(row, cell)}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </ReportSectionCard>
      </Box>
    </Box>
  );
};

export default AggregatorsPlReportPage;
